import * as React from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ApiClient } from '../../../lib/ApiClient';
import { UrlHelpers, RegexHelpers } from '../../../lib/Helpers';
import { useAuthenticated } from '../../auth/useAuthenticated'
import axios from 'axios';
import { getUrl, useDomainNavigate, domain } from '../../../domain';
import {mutateManage2fa} from './Manage2faEndpoints';
import { v4 as uuidv4 } from 'uuid';
import { logErrorGrayLog } from '../../../lib/ClientErrorGrayLogger';

const apiClient = new ApiClient();

export default function ManageProfile() {

    const userAuth = useAuthenticated(apiClient);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const delay = ms => new Promise(res => setTimeout(res, ms));

    // Get ReturnUrl from query string
    let returnUrl = UrlHelpers.getSafeUrl(new URLSearchParams(window.location.search).get('ReturnUrl'));

    let navigate = useDomainNavigate();
    if (!userAuth.isAuthenticating && !userAuth.isAuthenticated) {
        navigate("/account/login");
    }
    else if (userAuth.isAuthenticated && !userAuth.result.emailConfirmed)
    {
        navigate("/account/error/confirmEmail");
    }

    const handleCloseConfirmation = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }

        setOpenConfirmation(false);
    };

    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    const initialValues = {
        firstName: userAuth.result?.firstName,
        middleName: userAuth.result?.middleName,
        lastName: userAuth.result?.lastName,
        phoneNumber: userAuth.result?.phoneNumber,
        address: userAuth.result?.address
    };

    const validationSchema = yup.object({
        firstName: yup
            .string('Enter your First Name')
            .required("First Name is required")
            .nullable()
            .max(50),
        middleName: yup
            .string('Enter your Middle Name')
            .max(50)
            .nullable(),
        lastName: yup
            .string('Enter your Last Name')
            .required("Last name is required")
            .nullable()
            .max(50),
        phoneNumber: yup
            .string()
            .nullable()
            .matches(RegexHelpers.getPhoneRegex(), 'Phone number is not valid'),
        address: yup
            .string()
            .max(1024, 'Max of 1024 characters')
            .nullable(),
    });

    const redirectBack = async () => {
        await delay(3000);
        if (returnUrl != null) {
            window.location.replace(returnUrl);
        }
        else {
            navigate("/account/manage/2fa");
        }
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            axios.post(getUrl(`/identity/account/manage/profile`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    mutateManage2fa();
                    if (response.status === 200) {
                        userAuth.updateResult( response.data );
                        setOpenConfirmation(true);
                        redirectBack();
                    }
                    else  {
                        setErrors(response);
                    }
                    formik.setSubmitting(false);
                })
                .catch(error => {
                    if (Array.isArray(error.response.data)) {
                        setErrors(error.response.data);                    
                    }
                    else {
                        if (error?.response?.data?.errorMessage) {
                            setErrors([error?.response?.data?.errorMessage])    
                        }
                        else {
                            console.log("Unexpected error", error?.response, error?.response?.data);
                            const clientError = {
                                referenceGuid: uuidv4(),
                                clientError: JSON.stringify(error),
                                clientStacktrace: "N/A ManageProfile.js",
                                url: window?.location?.href
                            };                        

                            logErrorGrayLog(clientError);
                            setErrors(["Unexpected error: Reference " + clientError.referenceGuid]);
                        }
                    }
                    formik.setSubmitting(false);
                });
        },
    });

    return (
        <>
           <Box
                sx={{
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="subtitle1" gutterBottom component="div" sx={{mt:2}}>
                    Manage your profile.
                </Typography>
                <form onSubmit={formik.handleSubmit}
                >
                    {
                        errors.map((error) => (
                            <Alert sx={{ mt: 2 }} severity="error">{error}</Alert>
                        ))
                    }
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="firstName"
                        label="First Name"
                        fullWidth
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                        helperText={formik.touched.firstName && formik.errors.firstName}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="middleName"
                        label="Middle Name (optional)"
                        fullWidth
                        value={formik.values.middleName}
                        onChange={formik.handleChange}
                        error={formik.touched.middleName && Boolean(formik.errors.middleName)}
                        helperText={formik.touched.middleName && formik.errors.middleName}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="lastName"
                        label="Last Name"
                        fullWidth
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                        helperText={formik.touched.lastName && formik.errors.lastName}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="phoneNumber"
                        label="Phone Number (optional)"
                        fullWidth
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="address"
                        label="Address (optional)"
                        fullWidth
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        error={formik.touched.address && Boolean(formik.errors.address)}
                        helperText={formik.touched.address && formik.errors.address}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={formik.isSubmitting}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Save
                    </Button>
                </form>
            </Box>
            <Snackbar
                open={openConfirmation}
                autoHideDuration={6000}
                onClose={handleCloseConfirmation}
            >
                <Alert severity="success">
                    Your profile was updated successfully.
                    Redirecting...
                </Alert>
            </Snackbar>
        </>
    );
}